/**
 * 월간 스케줄 계획 현황 (오너용)
 * - 학생별 월간 계획 상태 요약
 * - 상세 캘린더 보기
 */
import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../config/api';
import MonthlyScheduleCalendar, { CalendarException } from './MonthlyScheduleCalendar';
import ScheduleChangeTable from './ScheduleChangeTable';

type TimeRange = { start: string; end: string };
type DayRanges = Record<string, TimeRange[]>;

interface PlanSummary {
  total_students: number;
  plans_created: number;
  draft: number;
  draft_auto: number;    // 지난달 일정이 자동 적용돼 달력이 이미 차 있음
  draft_empty: number;   // 계획도 일정도 비어 있음
  pending: number;
  approved: number;
  rejected: number;
  not_created: number;
  weekly_pending?: number;   // 주간 스케줄 변경 요청 대기 (월과 무관)
}

/** 화면에 보여줄 '스케줄 변경 요청' 한 줄 (어느 경로로 들어왔는지는 감춘다) */
interface PendingRow {
  key: string;
  source: 'weekly' | 'monthly';
  studentId: string;
  studentName: string;
  requestedAt: string | null;
  daysWaiting: number | null;
  planId?: string;
  /** 학부모가 동의한 요청인지 — 원장이 판단할 때 필요한 정보 */
  parentConsented: boolean;
  /** 누가 낸 요청인지 (학생/학부모) */
  requestedBy: '학생' | '학부모' | null;
  /** 승인 전에 '무엇이 바뀌는지' 보여주기 위한 요일별 시간 (주간 요청만) */
  currentRanges?: DayRanges | null;
  requestedRanges?: DayRanges | null;
}

/** 학생이 낸 주간 스케줄 변경 요청 — 학부모 승인 대기 상태 */
interface WeeklyPendingItem {
  id: string;
  student_id: string;
  student_name: string;
  effective_from: string | null;
  created_role: string | null;
  request_note: string | null;
  requested_at: string | null;
  days_waiting: number | null;
  current_ranges?: DayRanges | null;
  requested_ranges?: DayRanges | null;
}

interface PlanItem {
  id: string;
  student_id: string;
  student_name: string;
  year_month: string;
  status: string;
  submitted_at: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  has_schedule?: boolean;   // 달력에 실제 일정이 들어 있는지
}

interface PlanDetail {
  id: string;
  student_name: string;
  year_month: string;
  status: string;
  entries: {
    schedule_date: string;
    period_numbers: number[] | null;
    check_in_time: string | null;
    check_out_time: string | null;
    is_absent: boolean;
    absence_reason: string | null;
    note: string | null;
  }[];
}

const STATUS_LABELS: Record<string, string> = {
  draft: '작성 중',
  pending: '승인 대기',
  approved: '승인',
  rejected: '거절',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  pending: 'bg-orange-100 text-orange-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

// ⭐ 2026-07-31 — '작성 중'이 두 가지 뜻으로 섞여 있었다.
//   ① 학생이 아직 안 냈고 달력도 비어 있음 → 진짜 챙겨야 할 학생
//   ② 지난달 일정이 자동으로 이어져 달력은 이미 차 있음 → 그냥 둬도 되는 학생
//   달력 사전 생성 도입 후 ②가 100명 넘게 '작성 중'으로 잡혀 "다들 작성 안 했네"로 보였다.
const planLabel = (p: { status: string; has_schedule?: boolean }) =>
  p.status === 'draft' ? (p.has_schedule ? '기본 적용 중' : '미작성') : STATUS_LABELS[p.status];

const planColor = (p: { status: string; has_schedule?: boolean }) =>
  p.status === 'draft'
    ? (p.has_schedule ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600')
    : STATUS_COLORS[p.status];

const fmtDate = (v?: string | null) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : `${d.getMonth() + 1}/${d.getDate()}`;
};

export default function MonthlyPlanOverview() {
  const [yearMonth, setYearMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [summary, setSummary] = useState<PlanSummary | null>(null);
  const [weeklyPending, setWeeklyPending] = useState<WeeklyPendingItem[]>([]);
  const [approving, setApproving] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [plans, setPlans] = useState<PlanItem[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<PlanDetail | null>(null);
  const [planExceptions, setPlanExceptions] = useState<CalendarException[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  // 미작성 학생 등록 안내 푸시
  const [notifying, setNotifying] = useState(false);
  const [notifyResult, setNotifyResult] = useState<{ targeted: number; sent: number; undelivered: Array<{ name: string; user_number?: string; reason: string }> } | null>(null);

  const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');

  useEffect(() => {
    loadSummary();
  }, [yearMonth]);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/monthly-plans/organization/summary?year_month=${yearMonth}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setSummary(data.data.summary);
        setPlans(data.data.plans);
        setWeeklyPending(data.data.weekly_pending || []);
        // 탭·사이드바의 승인 대기 숫자도 다시 센다 (승인·거절 뒤엔 늘 이 함수를 다시 부른다)
        window.dispatchEvent(new Event('schedule-counts-changed'));
      }
    } catch (e) {
      console.error('월간 계획 조회 실패:', e);
    }
    setLoading(false);
  };

  // 학부모가 승인하지 않은 스케줄 변경 요청을 원장이 대신 승인.
  //   요청이 어느 경로로 들어왔든(주간 규칙/월간 계획) 버튼은 하나다 — 안에서 갈라 보낸다.
  const approvePending = async (item: PendingRow) => {
    if (!window.confirm(
      `${item.studentName} 학생의 스케줄 변경 요청을 승인하시겠습니까?\n\n` +
      (item.parentConsented
        ? `· 학부모가 동의한 요청입니다.\n`
        : `· 아직 학부모 동의가 없습니다. 원장님 판단으로 승인합니다.\n`) +
      `· 오늘까지는 지금 스케줄 그대로이고, 내일부터 바뀝니다.\n` +
      `· 오늘 당장 고쳐야 하면 학생 스케줄을 직접 저장해 주세요 — 그건 오늘부터 적용됩니다.`
    )) return;
    try {
      setApproving(item.key);
      const url = item.source === 'weekly'
        ? `${API_BASE_URL}/api/v1/owner/student-schedules/student/${item.studentId}/approve-pending`
        : `${API_BASE_URL}/api/v1/monthly-plans/${item.planId}/approve`;
      const res = await fetch(url,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const body = await res.json().catch(() => ({} as any));
      alert(`✅ ${body?.message || '승인 완료 — 내일부터 달력에 반영됩니다.'}`);
      await loadSummary();
    } catch (e) {
      alert(`승인 실패: ${e instanceof Error ? e.message : e}`);
    } finally {
      setApproving(null);
    }
  };

  const loadPlanDetail = async (planId: string, studentId: string) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/monthly-plans/${planId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setSelectedPlan(data.data);
      }
    } catch (e) {
      console.error('계획 상세 조회 실패:', e);
    }
    // 사유서는 달력 칸에 겹쳐 보여준다 — 조직 단위로 받아 그 학생 것만 남긴다
    setPlanExceptions([]);
    try {
      const r = await fetch(
        `${API_BASE_URL}/v1/owner/schedule-exceptions/requests?status=all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.ok) {
        const data = await r.json();
        const arr = Array.isArray(data) ? data : (data?.items || []);
        setPlanExceptions(arr.filter((e: any) => e.student_id === studentId));
      }
    } catch (e) {
      console.error('사유서 조회 실패:', e);
    }
  };

  const notifyUnregistered = async () => {
    if (!summary || summary.not_created <= 0) { alert('정기일정 미작성 학생이 없습니다.'); return; }
    if (!window.confirm(`정기일정 미작성 ${summary.not_created}명의 학부모에게 등록 안내 푸시를 보낼까요?\n\n· 학부모 앱으로 알림이 갑니다.\n· 학부모 없음/알림 미설정 학생은 '미도달 명단'으로 보여드립니다 (데스크·카톡방 직접 연락용).`)) return;
    setNotifying(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/monthly-plans/organization/notify-unregistered?year_month=${yearMonth}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (res.ok) setNotifyResult({ targeted: data.targeted, sent: data.sent, undelivered: data.undelivered || [] });
      else alert(data.detail || '발송 실패');
    } catch (e) {
      alert('발송 중 오류가 발생했습니다');
    } finally {
      setNotifying(false);
    }
  };

  const changeMonth = (delta: number) => {
    const [y, m] = yearMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setYearMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    setSelectedPlan(null);
  };

  const byStatus = (statusFilter === 'all'
    ? plans
    : plans.filter(p => p.status === statusFilter)
  ).filter(p => p.status !== 'pending');   // 대기 건은 위 통합 목록에서만 보여준다
  const filteredPlans = search.trim()
    ? byStatus.filter(p => (p.student_name || '').toLowerCase().includes(search.trim().toLowerCase()))
    : byStatus;
  // ⭐ 승인 대기는 **한 줄로 합친다.**
  //   원장님은 '주간/월간' 구분을 모르고 그냥 '스케줄'로 안다. 요청이 어느 경로로
  //   들어왔는지는 화면에 드러내지 않고, 같은 줄에 같은 버튼으로만 보여준다.
  const pendingRows: PendingRow[] = [
    ...weeklyPending.map(w => ({
      key: `w-${w.id}`,
      source: 'weekly' as const,
      studentId: w.student_id,
      studentName: w.student_name,
      requestedAt: w.requested_at,
      daysWaiting: w.days_waiting,
      // 학부모가 직접 낸 요청이면 그 자체가 동의. 학생 요청은 학부모가 눌러야 표시된다.
      parentConsented: w.created_role === 'parent' || (w.request_note || '').includes('학부모가 동의'),
      requestedBy: (w.created_role === 'parent' ? '학부모' : w.created_role === 'student' ? '학생' : null) as PendingRow['requestedBy'],
      currentRanges: w.current_ranges ?? null,
      requestedRanges: w.requested_ranges ?? null,
    })),
    ...plans.filter(p => p.status === 'pending').map(p => ({
      key: `m-${p.id}`,
      source: 'monthly' as const,
      studentId: p.student_id,
      studentName: p.student_name,
      requestedAt: p.submitted_at,
      daysWaiting: p.submitted_at
        ? Math.max(0, Math.floor((Date.now() - new Date(p.submitted_at).getTime()) / 86400000))
        : null,
      planId: p.id,
      parentConsented: (p.review_note || '').includes('학부모가 동의'),
      requestedBy: '학생' as PendingRow['requestedBy'],
    })),
  ].sort((a, b) => (b.daysWaiting ?? 0) - (a.daysWaiting ?? 0));

  const visiblePending = search.trim()
    ? pendingRows.filter(r => (r.studentName || '').toLowerCase().includes(search.trim().toLowerCase()))
    : pendingRows;

  if (selectedPlan) {
    return (
      <div>
        <button
          onClick={() => setSelectedPlan(null)}
          className="text-blue-600 hover:text-blue-800 mb-4 flex items-center gap-1"
        >
          &larr; 목록으로
        </button>
        <PlanCalendarView plan={selectedPlan} exceptions={planExceptions} />
      </div>
    );
  }

  return (
    <div>
      {/* 월 선택 */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => changeMonth(-1)} className="p-2 hover:bg-gray-100 rounded">
          &larr;
        </button>
        <h3 className="text-lg font-bold">
          {yearMonth.replace('-', '년 ')}월 월간 계획
        </h3>
        <button onClick={() => changeMonth(1)} className="p-2 hover:bg-gray-100 rounded">
          &rarr;
        </button>
      </div>

      {/* 요약 카드 */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <SummaryCard label="승인" value={summary.approved} color="text-green-600" bg="bg-green-50" />
          <SummaryCard label="대기" value={summary.pending + (summary.weekly_pending ?? 0)} color="text-orange-600" bg="bg-orange-50" />
          <SummaryCard label="기본 적용 중" value={summary.draft_auto ?? summary.draft} color="text-blue-600" bg="bg-blue-50" />
          <SummaryCard label="미작성" value={summary.not_created + (summary.draft_empty ?? 0)} color="text-red-600" bg="bg-red-50" />
        </div>
      )}

      {/* 미작성 학생 등록 안내 발송 */}
      {summary && summary.not_created > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4">
          <span className="text-sm text-red-700">정기일정 <b>미작성 {summary.not_created}명</b> — 학부모에게 등록 안내 알림을 보낼 수 있어요.</span>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={notifyUnregistered} disabled={notifying}
              className="bg-red-600 text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50 whitespace-nowrap">
              {notifying ? '발송 중...' : '📣 푸시 발송'}
            </button>
            <button disabled title="카카오 알림톡은 템플릿 승인 후 제공됩니다"
              className="bg-gray-100 text-gray-400 rounded-lg px-4 py-2 text-sm font-semibold whitespace-nowrap cursor-not-allowed border border-gray-200">
              📩 알림톡 발송 <span className="text-xs">(준비 중)</span>
            </button>
          </div>
        </div>
      )}

      {/* 발송 결과 모달 */}
      {notifyResult && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setNotifyResult(null)}>
          <div className="bg-white rounded-xl max-w-md w-full max-h-[80vh] overflow-y-auto p-5" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-2">📣 등록 안내 발송 결과</h3>
            <p className="text-sm text-gray-700 mb-1">
              대상(미작성) <b>{notifyResult.targeted}명</b> · 발송 성공 <b className="text-green-600">{notifyResult.sent}명</b> · 미도달 <b className="text-red-600">{notifyResult.undelivered.length}명</b>
            </p>
            {notifyResult.undelivered.length > 0 ? (
              <div className="mt-3">
                <p className="text-xs text-gray-500 mb-1">아래 학생은 푸시가 안 갔어요(학부모 없음/알림 미설정). 데스크·카카오채널로 직접 안내해주세요.</p>
                <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                  {notifyResult.undelivered.map((u, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span>{u.name} {u.user_number && <span className="text-gray-400 text-xs">{u.user_number}</span>}</span>
                      <span className="text-xs text-red-500 whitespace-nowrap">{u.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-green-600 mt-2">대상 학생 모두에게 발송됐어요 🎉</p>
            )}
            <button onClick={() => setNotifyResult(null)} className="mt-4 w-full bg-gray-100 rounded-lg py-2 text-sm font-medium">닫기</button>
          </div>
        </div>
      )}

      {/* 필터 */}
      <div className="flex gap-2 mb-4">
        {['all', 'approved', 'pending', 'draft', 'rejected'].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              statusFilter === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {s === 'all' ? '전체' : s === 'draft' ? '기본 적용/미작성' : STATUS_LABELS[s]}
            {s !== 'all' && ` (${s === 'pending' ? pendingRows.length : plans.filter(p => p.status === s).length})`}
          </button>
        ))}
      </div>

      {/* 🔎 학생 검색 */}
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="🔎 학생 이름 검색"
        className="w-full mb-4 border rounded-lg px-3 py-2 text-sm"
      />

      {/* ⭐ 스케줄 변경 요청 — 학부모가 승인 안 하면 원장이 볼 곳이 여기뿐이다.
             요청 경로(주간 규칙/월간 계획)는 화면에 드러내지 않고 한 줄로 합쳐 보여준다.
             월 단위가 아닌 요청도 있으므로 보고 있는 달과 무관하게 항상 노출한다. */}
      {!loading && (statusFilter === 'all' || statusFilter === 'pending') && visiblePending.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-semibold text-gray-800">스케줄 변경 요청</span>
            <span className="text-xs bg-orange-100 text-orange-700 rounded-full px-2 py-0.5">{visiblePending.length}건</span>
            <span className="text-xs text-gray-400">원장님 확인이 필요합니다 (승인해야 적용)</span>
          </div>
          <div className="space-y-2">
            {visiblePending.map(r => {
              const stale = (r.daysWaiting ?? 0) >= 7;
              const open = expanded === r.key;
              return (
                <div key={r.key}
                  className={`p-3 rounded-lg border ${stale ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="min-w-0 cursor-pointer" onClick={() => setExpanded(open ? null : r.key)}>
                    <span className="font-semibold text-gray-900">{r.studentName}</span>
                    <span className="ml-1 text-gray-400 text-xs">{open ? '▲' : '▼'}</span>
                    <span className={`ml-2 text-[11px] rounded-full px-2 py-0.5 ${r.parentConsented ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {r.parentConsented ? '학부모 동의함' : '학부모 미확인'}
                    </span>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {r.requestedBy && <span>{r.requestedBy} 요청 · </span>}
                      요청 {fmtDate(r.requestedAt) ?? '-'}
                      {r.daysWaiting != null && (
                        <span className={stale ? 'text-red-600 font-semibold' : ''}> · {r.daysWaiting}일 대기</span>
                      )}
                      {/* 적용일은 요청서에 적힌 날짜가 아니라 '승인한 날의 다음 날'이다(2026-08-13,
                          2026-08-18 재확인). 요청 날짜를 '적용 예정'으로 보여주면 확인창과 어긋난다.
                          ⭐ 원장이 직접 저장하는 경로는 '당일 적용'이라 문구가 다르다 — 헷갈리지 말 것. */}
                      <span> · 승인하면 다음 날부터 적용</span>
                    </div>
                  </div>
                  <button
                    onClick={() => approvePending(r)}
                    disabled={approving === r.key}
                    className="px-3 py-1.5 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap flex-shrink-0"
                  >
                    {approving === r.key ? '승인 중…' : '승인'}
                  </button>
                </div>
                {/* 🔴 승인 전에 '무엇이 바뀌는지' 보여준다 (2026-08-14 송파점 문의) */}
                {open && (
                  <div className="border-t border-orange-200 mt-2 pt-2">
                    {r.source === 'weekly' ? (
                      r.requestedRanges ? (
                        <ScheduleChangeTable current={r.currentRanges} next={r.requestedRanges} compact />
                      ) : (
                        <div className="text-xs text-gray-500">요청 내용을 불러오지 못했습니다.</div>
                      )
                    ) : (
                      <button
                        onClick={() => r.planId && loadPlanDetail(r.planId, r.studentId)}
                        className="text-xs text-blue-600 underline"
                      >
                        달력에서 요청 내용 보기
                      </button>
                    )}
                  </div>
                )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 학생 목록 */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">로딩 중...</div>
      ) : filteredPlans.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          {statusFilter === 'pending' ? (visiblePending.length > 0 ? '' : '승인 대기 중인 요청이 없습니다')
            : search.trim() ? `'${search}' 검색 결과가 없습니다`
            : statusFilter === 'all' ? '이번 달 계획이 없습니다' : `${STATUS_LABELS[statusFilter]} 상태의 계획이 없습니다`}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredPlans.map(plan => (
            <div
              key={plan.id}
              className="flex items-center justify-between p-3 bg-white rounded-lg border hover:border-blue-300"
            >
              <div className="flex-1 cursor-pointer" onClick={() => loadPlanDetail(plan.id, plan.student_id)}>
                <span className="font-semibold text-gray-900">{plan.student_name}</span>
                {(fmtDate(plan.submitted_at) || fmtDate(plan.reviewed_at)) && (
                  <span className="text-xs text-gray-400 ml-2">
                    {fmtDate(plan.submitted_at) && `제출 ${fmtDate(plan.submitted_at)}`}
                    {fmtDate(plan.submitted_at) && fmtDate(plan.reviewed_at) && ' · '}
                    {fmtDate(plan.reviewed_at) && `승인 ${fmtDate(plan.reviewed_at)}`}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${planColor(plan)}`}>
                  {planLabel(plan)}
                </span>
                {(plan.status === 'draft' || plan.status === 'pending' || plan.status === 'rejected') && (
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!window.confirm(`${plan.student_name} 학생의 ${planLabel(plan)} 상태 월간 계획을 강제 승인하시겠습니까?\n(학부모 없는 성인 학생용)`)) return;
                      const res = await fetch(`${API_BASE_URL}/api/v1/monthly-plans/${plan.id}/approve`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      if (res.ok) {
                        alert('✅ 강제 승인 완료');
                        await loadSummary();
                      } else {
                        const err = await res.json().catch(() => ({}));
                        alert(`승인 실패: ${err.detail || res.status}`);
                      }
                    }}
                    className="px-2 py-1 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-700"
                  >
                    강제 승인
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color, bg }: { label: string; value: number; color: string; bg: string }) {
  return (
    <div className={`${bg} rounded-lg p-3 text-center`}>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  );
}

function PlanCalendarView({ plan, exceptions }: { plan: PlanDetail; exceptions: CalendarException[] }) {
  const [y, m] = plan.year_month.split('-').map(Number);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold">{plan.student_name}</h3>
          <p className="text-sm text-gray-500">{y}년 {m}월</p>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[plan.status]}`}>
          {STATUS_LABELS[plan.status]}
        </span>
      </div>

      {/* 요약 */}
      <div className="flex gap-3 mb-4">
        <span className="text-sm text-blue-600 font-medium">
          출석 {plan.entries.filter(e => !e.is_absent && (e.period_numbers?.length || e.check_in_time)).length}일
        </span>
        <span className="text-sm text-red-600 font-medium">
          결석 {plan.entries.filter(e => e.is_absent).length}일
        </span>
      </div>

      <MonthlyScheduleCalendar
        year={y}
        month={m}
        entries={plan.entries}
        exceptions={exceptions}
      />
    </div>
  );
}
